import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { siteConfig, navLinks, websitesMenu } from '../data/mock';
import { sectors } from '../data/sectors';

// Lead with our priority prospects: charities and care-tech suppliers.
const SECTOR_ORDER = ['charities', 'care-technology', 'health-and-social-care', 'professional-services', 'tech-and-saas', 'wellbeing', 'education-and-send', 'e-commerce'];
const orderedSectors = SECTOR_ORDER.map((sl) => sectors.find((s) => s.slug === sl)).filter(Boolean);

// Extra links shown in the desktop SECTORS dropdown, kept here so the mobile menu stays in sync.
const MobileDropdown = ({ id, label, to, items, open, onToggle, location, onNavigate }) => {
  const anyActive = items.some((it) => location.pathname === it.to) || (to && location.pathname.startsWith(to));
  return (
    <div style={{ width: 280, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
      <button
        onClick={() => onToggle(id)}
        data-testid={`mobile-dd-${id}`}
        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'none', border: 'none', cursor: 'pointer', padding: '14px 4px', fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '1.1rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: anyActive ? 'var(--teal)' : 'var(--dark)' }}
      >
        {label}
        <ChevronDown size={18} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease', flexShrink: 0 }} />
      </button>
      {open && (
        <div data-testid={`mobile-dd-${id}-panel`} style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 4px 16px' }}>
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              onClick={onNavigate}
              data-testid={`mobile-dd-${id}-${it.to.replace(/\//g, '')}`}
              style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '0.92rem', letterSpacing: '0.05em', textTransform: 'uppercase', color: it.pink ? 'var(--hot-pink)' : (location.pathname === it.to ? 'var(--teal)' : 'var(--body-text)') }}
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const [websitesOpen, setWebsitesOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <nav
        data-testid="main-navbar"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          padding: isScrolled ? '8px 0' : '14px 0',
          background: 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(12px)',
          boxShadow: isScrolled ? '0 1px 16px rgba(0,0,0,0.06)' : 'none',
          transition: 'padding 0.3s ease, box-shadow 0.3s ease',
        }}
      >
        <div style={{ maxWidth: 1560, margin: '0 auto', padding: '0 48px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginRight: 16 }} data-testid="logo-link">
            <img
              src={siteConfig.logo}
              alt="Blue Cactus Digital"
              style={{ height: isScrolled ? 26 : 30, transition: 'height 0.3s ease' }}
            />
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} className="desktop-nav">
            {navLinks.map((link) => (
              link.label === 'WEBSITES' ? (
                <div key={link.path} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }} onMouseEnter={() => setWebsitesOpen(true)} onMouseLeave={() => setWebsitesOpen(false)}>
                  <Link to="/websites" data-testid="nav-websites"
                    style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1, gap: 3, fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.02em', whiteSpace: 'nowrap', color: ['/websites', '/website-builder', '/bespoke-crm-systems'].includes(location.pathname) ? 'var(--teal)' : 'var(--dark)', textTransform: 'uppercase', transition: 'color 0.25s ease' }}>
                    WEBSITES <ChevronDown size={13} style={{ transform: websitesOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
                  </Link>
                  {websitesOpen && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, paddingTop: 8, minWidth: 210 }}>
                      <div data-testid="nav-websites-dropdown" style={{ background: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', borderRadius: 8, padding: '8px 0', display: 'flex', flexDirection: 'column' }}>
                        {websitesMenu.map((w) => (
                          <Link key={w.path} to={w.path} data-testid={`nav-websites-${w.path.replace(/\//g, '')}`}
                            style={{ padding: '10px 20px', fontFamily: 'var(--font-heading)', fontSize: '12px', letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--dark)', whiteSpace: 'nowrap', transition: 'background 0.2s ease, color 0.2s ease' }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--off-white)'; e.currentTarget.style.color = 'var(--teal)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dark)'; }}>
                            {w.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 400,
                  fontSize: '12px',
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  lineHeight: 1,
                  color: location.pathname === link.path ? 'var(--teal)' : 'var(--dark)',
                  textTransform: 'uppercase',
                  transition: 'color 0.25s ease',
                }}
                onMouseEnter={(e) => { e.target.style.color = 'var(--teal)'; }}
                onMouseLeave={(e) => {
                  e.target.style.color = location.pathname === link.path ? 'var(--teal)' : 'var(--dark)';
                }}
              >
                {link.label}
              </Link>
              )
            ))}

            {/* Sectors dropdown */}
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }} onMouseEnter={() => setSectorsOpen(true)} onMouseLeave={() => setSectorsOpen(false)}>
              <Link to="/sectors" data-testid="nav-sectors"
                style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1, gap: 3, fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.02em', whiteSpace: 'nowrap', color: location.pathname.startsWith('/sectors') ? 'var(--teal)' : 'var(--dark)', textTransform: 'uppercase', transition: 'color 0.25s ease' }}>
                SECTORS <ChevronDown size={13} style={{ transform: sectorsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
              </Link>
              {sectorsOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, paddingTop: 8, minWidth: 250 }}>
                <div data-testid="nav-sectors-dropdown" style={{ background: '#fff', boxShadow: '0 10px 30px rgba(0,0,0,0.12)', borderRadius: 8, padding: '8px 0', display: 'flex', flexDirection: 'column' }}>
                  {orderedSectors.map((s) => (
                    <Link key={s.slug} to={`/sectors/${s.slug}`} data-testid={`nav-sector-${s.slug}`}
                      style={{ padding: '10px 20px', fontFamily: 'var(--font-heading)', fontSize: '12px', letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--dark)', whiteSpace: 'nowrap', transition: 'background 0.2s ease, color 0.2s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--off-white)'; e.currentTarget.style.color = 'var(--teal)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--dark)'; }}>
                      {s.navLabel}
                    </Link>
                  ))}
                </div>
                </div>
              )}
            </div>

            <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} data-testid="nav-phone" title={`Call ${siteConfig.phone}`}
              style={{ display: 'none' }} aria-hidden="true">{siteConfig.phone}</a>
            <Link
              to="/courses"
              data-testid="nav-academy-btn"
              style={{
                fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.02em',
                padding: '9px 15px', background: 'var(--academy-blue)', color: 'var(--white)', border: '2px solid var(--academy-blue)',
                textTransform: 'uppercase', transition: 'background 0.3s ease', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#2e3170'; e.currentTarget.style.borderColor = '#2e3170'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--academy-blue)'; e.currentTarget.style.borderColor = 'var(--academy-blue)'; }}
            >
              ACADEMY
            </Link>
            <Link
              to="/quiz"
              data-testid="nav-free-quote-btn"
              style={{
                fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '12px', letterSpacing: '0.02em',
                padding: '9px 15px', background: 'var(--teal)', color: 'var(--white)', border: '2px solid var(--teal)',
                textTransform: 'uppercase', transition: 'background 0.3s ease', cursor: 'pointer', whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#2c8195'; e.currentTarget.style.borderColor = '#2c8195'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--teal)'; e.currentTarget.style.borderColor = 'var(--teal)'; }}
            >
              FREE QUOTE
            </Link>
            <a
              href="/book-online"
              data-testid="book-a-call-btn"
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 400,
                fontSize: '12px',
                letterSpacing: '0.02em',
                padding: '9px 15px',
                background: 'var(--hot-pink)',
                color: 'var(--white)',
                border: '2px solid var(--hot-pink)',
                textTransform: 'uppercase',
                transition: 'background 0.3s ease',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#e56d98'; e.currentTarget.style.borderColor = '#e56d98'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--hot-pink)'; e.currentTarget.style.borderColor = 'var(--hot-pink)'; }}
            >
              BOOK A CALL
            </a>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--dark)', padding: 8 }}
          >
            {mobileOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div data-testid="mobile-menu-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'var(--white)', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: 20, overflowY: 'auto', padding: '84px 20px 48px', WebkitOverflowScrolling: 'touch' }}>
          <button onClick={() => setMobileOpen(false)} style={{ position: 'absolute', top: 20, right: 40, background: 'none', border: 'none', color: 'var(--dark)', cursor: 'pointer' }}>
            <X size={32} />
          </button>
          {navLinks.map((link) => (
            link.label === 'WEBSITES' ? (
              <MobileDropdown
                key="websites"
                id="websites"
                label="WEBSITES"
                to="/websites"
                items={websitesMenu.map((w) => ({ to: w.path, label: w.label }))}
                open={mobileSub === 'websites'}
                onToggle={(k) => setMobileSub((p) => (p === k ? null : k))}
                location={location}
                onNavigate={() => setMobileOpen(false)}
              />
            ) : (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                style={{ width: 280, padding: '14px 4px', borderBottom: '1px solid rgba(0,0,0,0.08)', fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '1.1rem', letterSpacing: '0.08em', color: location.pathname === link.path ? 'var(--teal)' : 'var(--dark)', textTransform: 'uppercase' }}
              >
                {link.label}
              </Link>
            )
          ))}

          <MobileDropdown
            id="sectors"
            label="SECTORS"
            to="/sectors"
            items={orderedSectors.map((s) => ({ to: `/sectors/${s.slug}`, label: s.navLabel }))}
            open={mobileSub === 'sectors'}
            onToggle={(k) => setMobileSub((p) => (p === k ? null : k))}
            location={location}
            onNavigate={() => setMobileOpen(false)}
          />

          <Link to="/courses" onClick={() => setMobileOpen(false)} data-testid="mobile-academy-btn" style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '13px', letterSpacing: '0.04em', padding: '12px 28px', background: 'var(--academy-blue)', color: 'var(--white)', textTransform: 'uppercase', marginTop: 20 }}>
            ACADEMY
          </Link>
          <Link to="/quiz" onClick={() => setMobileOpen(false)} data-testid="mobile-free-quote-btn" style={{ fontFamily: 'var(--font-heading)', fontWeight: 400, fontSize: '13px', letterSpacing: '0.04em', padding: '12px 28px', background: 'var(--teal)', color: 'var(--white)', textTransform: 'uppercase', marginTop: 20 }}>
            GET A FREE QUOTE
          </Link>
          <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer" className="btn-pink-filled">
            BOOK A CALL
          </a>
          <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} data-testid="mobile-nav-phone" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-heading)', fontSize: '15px', color: 'var(--dark)', textDecoration: 'none' }}>
            <Phone size={16} /> {siteConfig.phone}
          </a>
        </div>
      )}
    </>
  );
};

export default Navbar;
